import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { toolFormSchema, type ToolFormData } from '@/schemas/toolSchemas';
import type { Tool } from '@/types/legacy';
import { canViewMoney } from '@/lib/roleUtils';
import { Loader2 } from 'lucide-react';

interface ToolFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tool?: Tool | null;
  onSuccess?: () => void;
}

export function ToolFormDialog({ open, onOpenChange, tool, onSuccess }: ToolFormDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { userRole } = useAuth();
  const canSeeMoney = canViewMoney(userRole);
  const [loading, setLoading] = useState(false);

  const form = useForm<ToolFormData>({
    resolver: zodResolver(toolFormSchema),
    defaultValues: {
      name: tool?.name || '',
      code: tool?.code || '',
      type: tool?.type || '',
      description: tool?.description || '',
      location: tool?.location || '',
      status: tool?.status || 'available',
      quantity: tool?.quantity || 1,
      min_quantity: tool?.min_quantity || 1,
      cost_per_unit: tool?.cost_per_unit,
      purchase_date: tool?.purchase_date || '',
      supplier: tool?.supplier || '',
      maintenance_schedule: tool?.maintenance_schedule || '',
      last_maintenance: tool?.last_maintenance || '',
      notes: tool?.notes || '',
      is_active: tool?.is_active ?? true,
    },
  });
  // Reset form when tool changes or dialog opens
  useEffect(() => {
    if (open) {
      form.reset({
        name: tool?.name || '',
      code: tool?.code || '',
      type: tool?.type || '',
      description: tool?.description || '',
      location: tool?.location || '',
      status: tool?.status || 'available',
      quantity: tool?.quantity || 1,
      min_quantity: tool?.min_quantity || 1,
      cost_per_unit: tool?.cost_per_unit,
      purchase_date: tool?.purchase_date || '',
      supplier: tool?.supplier || '',
      maintenance_schedule: tool?.maintenance_schedule || '',
      last_maintenance: tool?.last_maintenance || '',
      notes: tool?.notes || '',
      is_active: tool?.is_active ?? true,
      });
    }
  }, [open, tool, form]);

const onSubmit = async (data: ToolFormData) => {
    setLoading(true);
    try {
      const payload = {
        name: data.name,
        code: data.code || null,
        type: data.type || null,
        description: data.description || null,
        location: data.location || null,
        status: data.status,
        quantity: data.quantity,
        min_quantity: data.min_quantity,
        cost_per_unit: data.cost_per_unit || null,
        purchase_date: data.purchase_date || null,
        supplier: data.supplier || null,
        maintenance_schedule: data.maintenance_schedule || null,
        last_maintenance: data.last_maintenance || null,
        notes: data.notes || null,
        is_active: data.is_active,
      };

      if (tool) {
        const { error } = await api
          .from('tools')
          .update(payload)
          .eq('id', tool.id);

        if (error) throw error;

        toast({
          title: 'Herramienta actualizada',
          description: 'La herramienta se actualizó correctamente',
        });
      } else {
        const { error } = await api
          .from('tools')
          .insert([payload]);

        if (error) throw error;

        toast({
          title: 'Herramienta creada',
          description: 'La herramienta se creó correctamente',
        });
      }

      queryClient.invalidateQueries({ queryKey: ['tools'] });
      onOpenChange(false);
      form.reset();
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar la herramienta',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{tool ? 'Editar Herramienta' : 'Nueva Herramienta'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Molino CNC" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Código</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: TOOL-001" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Corte, Medición, etc." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estado</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar estado" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="available">Disponible</SelectItem>
                        <SelectItem value="in_use">En Uso</SelectItem>
                        <SelectItem value="maintenance">En Mantenimiento</SelectItem>
                        <SelectItem value="damaged">Dañada</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Descripción de la herramienta" rows={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cantidad *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : 1)}
                        min={1}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="min_quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cantidad Mínima *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : 1)}
                        min={1}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {canSeeMoney && (
                <FormField
                  control={form.control}
                  name="cost_per_unit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Costo Unitario</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                          step="0.01"
                          min={0}
                          placeholder="0.00"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ubicación</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Almacén A - Estante 3" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="supplier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Proveedor</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Nombre del proveedor" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="purchase_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha de Compra</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="last_maintenance"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Último Mantenimiento</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="maintenance_schedule"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Programa de Mantenimiento</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Ej: Cada 6 meses" />
                  </FormControl>
                  <FormDescription>
                    Frecuencia recomendada de mantenimiento
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Notas adicionales" rows={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Activa</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      La herramienta está disponible en el sistema
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {tool ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
